import {LIVE_MARKER_TEXTURE_KEYS} from '../../configs/liveFieldMarkers';
import {buildMedallionPixels, rasterizePixels} from './sizeRecruitmentTextures';

/**
 * Текстуры живых маркеров строений (см. configs/liveFieldMarkers): пиксели
 * считаются кодом и регистрируются через textures.addUint8Array. Пламя и дуги
 * белые, красятся tint; медальоны и шеврон — в своих цветах.
 */

export const LIVE_MARKER_TEXTURE_SIZES = {
  [LIVE_MARKER_TEXTURE_KEYS.FLAME]: {width: 64, height: 128},
  [LIVE_MARKER_TEXTURE_KEYS.EMBER_PLATE]: {width: 128, height: 128},
  [LIVE_MARKER_TEXTURE_KEYS.BLOCK_PLATE]: {width: 128, height: 128},
  [LIVE_MARKER_TEXTURE_KEYS.BLOCK_ARCS]: {width: 128, height: 128},
  [LIVE_MARKER_TEXTURE_KEYS.FORTRESS_CHEVRON]: {width: 128, height: 256},
};

const EMBER_PLATE_COLORS = {
  rimLight: [255, 214, 120],
  rim: [255, 122, 28],
  rimDark: [150, 42, 8],
  groove: [30, 12, 4],
  line: [214, 92, 24],
  insideTop: [58, 30, 20],
  insideBottom: [26, 12, 8],
  insideGlow: [122, 44, 14],
  shadow: [16, 7, 2],
};

const BLOCK_PLATE_COLORS = {
  rimLight: [255, 170, 150],
  rim: [226, 42, 34],
  rimDark: [116, 12, 12],
  groove: [26, 6, 8],
  line: [186, 44, 40],
  insideTop: [56, 22, 26],
  insideBottom: [24, 8, 12],
  insideGlow: [98, 22, 26],
  shadow: [14, 4, 4],
};

// Цвета шеврона — те же, что у золотого медальона
const CHEVRON_COLORS = {
  rimLight: [255, 240, 170],
  rim: [255, 190, 40],
  rimDark: [176, 104, 12],
  inside: [30, 20, 12],
  shadow: [18, 9, 2],
};

export const buildEmberPlatePixels = () => buildMedallionPixels(
  LIVE_MARKER_TEXTURE_SIZES[LIVE_MARKER_TEXTURE_KEYS.EMBER_PLATE].width, EMBER_PLATE_COLORS,
);

export const buildBlockPlatePixels = () => buildMedallionPixels(
  LIVE_MARKER_TEXTURE_SIZES[LIVE_MARKER_TEXTURE_KEYS.BLOCK_PLATE].width, BLOCK_PLATE_COLORS,
);

// Полуширина языка пламени на высоте t (0 — кончик, 1 — низ), доля ширины картинки
const flameHalfWidth = (t) => {
  const widest = 0.68;
  return t <= widest
    ? 0.4 * (t / widest) ** 1.25
    : 0.4 * Math.sqrt(Math.max(0, 1 - ((t - widest) / (1 - widest)) ** 2));
};

/** Язык пламени: кончик вверху и чуть в сторону, низ круглый */
export const buildFlamePixels = () => {
  const {width, height} = LIVE_MARKER_TEXTURE_SIZES[LIVE_MARKER_TEXTURE_KEYS.FLAME];
  return rasterizePixels(width, height, (x, y) => {
    const t = (y + height / 2 - height * 0.03) / (height * 0.94);
    if (t < 0 || t > 1) {
      return [255, 255, 255, 0];
    }
    const center = 0.07 * Math.sin(t * Math.PI * 1.4) * (1 - t);
    return [255, 255, 255, Math.abs(x / width - center) <= flameHalfWidth(t) ? 1 : 0];
  });
};

const ARC_COUNT = 3;
const ARC_FILL_SHARE = 0.68;

/** Три дуги кольца с зазорами */
export const buildBlockArcsPixels = () => {
  const {width, height} = LIVE_MARKER_TEXTURE_SIZES[LIVE_MARKER_TEXTURE_KEYS.BLOCK_ARCS];
  const outer = width / 2 - 2;
  const inner = outer - 6;
  return rasterizePixels(width, height, (x, y) => {
    const radius = Math.hypot(x, y);
    if (radius < inner || radius > outer) {
      return [255, 255, 255, 0];
    }
    const turn = ((Math.atan2(y, x) / (Math.PI * 2) + 1) * ARC_COUNT) % 1;
    return [255, 255, 255, turn < ARC_FILL_SHARE ? 1 : 0];
  });
};

// Ломаная шеврона на картинке 128 × 256 (от ее центра): концы справа, вершина слева
const CHEVRON_TOP = {x: 28, y: -90};
const CHEVRON_APEX = {x: -28, y: 0};
const CHEVRON_BOTTOM = {x: 28, y: 90};
const CHEVRON_HALF_WIDTH = 24;
const CHEVRON_RIM_WIDTH = 6;
const CHEVRON_SHADOW_WIDTH = 10;

const distanceToSegment = (x, y, from, to) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const along = Math.max(0, Math.min(1, ((x - from.x) * dx + (y - from.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(x - from.x - dx * along, y - from.y - dy * along);
};

const mix = (from, to, share) => from.map((channel, index) => channel + (to[index] - channel) * share);

/** Шеврон шкалы крепости: золотая оправа, темное поле, вокруг темная тень */
export const buildFortressChevronPixels = () => {
  const {width, height} = LIVE_MARKER_TEXTURE_SIZES[LIVE_MARKER_TEXTURE_KEYS.FORTRESS_CHEVRON];
  const colors = CHEVRON_COLORS;
  return rasterizePixels(width, height, (x, y) => {
    const distance = Math.min(
      distanceToSegment(x, y, CHEVRON_TOP, CHEVRON_APEX),
      distanceToSegment(x, y, CHEVRON_APEX, CHEVRON_BOTTOM),
    ) - CHEVRON_HALF_WIDTH;
    if (distance > 0) {
      const fade = 1 - distance / CHEVRON_SHADOW_WIDTH;
      return fade > 0 ? [...colors.shadow, 0.65 * fade * fade] : [0, 0, 0, 0];
    }
    if (-distance < CHEVRON_RIM_WIDTH) {
      const heightShare = (y + height / 2) / height;
      const rim = heightShare < 0.5
        ? mix(colors.rimLight, colors.rim, heightShare * 2)
        : mix(colors.rim, colors.rimDark, heightShare * 2 - 1);
      return [...rim, 1];
    }
    return [...colors.inside, 1];
  });
};

const PIXEL_BUILDERS = {
  [LIVE_MARKER_TEXTURE_KEYS.FLAME]: buildFlamePixels,
  [LIVE_MARKER_TEXTURE_KEYS.EMBER_PLATE]: buildEmberPlatePixels,
  [LIVE_MARKER_TEXTURE_KEYS.BLOCK_PLATE]: buildBlockPlatePixels,
  [LIVE_MARKER_TEXTURE_KEYS.BLOCK_ARCS]: buildBlockArcsPixels,
  [LIVE_MARKER_TEXTURE_KEYS.FORTRESS_CHEVRON]: buildFortressChevronPixels,
};

/**
 * Зарегистрировать текстуры один раз на сцену
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR
 * @returns {boolean} готовы ли текстуры
 */
export const ensureLiveMarkerTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(LIVE_MARKER_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = LIVE_MARKER_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
